import React from 'react';
import { Grid, TextField, Tooltip } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { FormikState } from 'formik';
import { ReactComponent as PaymentLinkPaidIcon } from '../../../images/paymentLinkIcons/mb-pl-paid.svg';
import { ReactComponent as PaymentLinkWaitingIcon } from '../../../images/paymentLinkIcons/mb-pl-waiting.svg';
import { ReactComponent as PaymentLinkArchiveIcon } from '../../../images/paymentLinkIcons/mb-pl-archive.svg';
import { useEditOrder } from './EditOrderContext';
import UiObjectsList from '../../../editOrderUiObjects/UiObjectsList';
import { useAuth } from '../../../session/InternalAuthProvider';
import { AccountData, OrderData, PaymentLinkPaymentStatus, VoucherType } from '../../../api';
import { PriceBox } from './components/experienceDetailPanel/PriceBox';
import { ExtraItemsQuickAction } from './components/experienceDetailPanel/ExtraItemsQuickAction';
import { getCurrencySymbol } from '../../../utils/utils';

export const getPaymentLinkStatus = (status: PaymentLinkPaymentStatus, translations: any, archived?: boolean) => {
  if (archived) {
    return (
      <Tooltip title={translations('archived')} arrow>
        <span style={{ display: 'inline-flex', opacity: 0.5 }}>
          <PaymentLinkArchiveIcon width={24} height={24} />
        </span>
      </Tooltip>
    );
  }

  switch (status) {
    case PaymentLinkPaymentStatus.PAID:
      return (
        <Tooltip title={translations('calendar.edit_order.paymentLinkStatusPaid')} arrow>
          <span style={{ display: 'inline-flex' }}>
            <PaymentLinkPaidIcon width={24} height={24} />
          </span>
        </Tooltip>
      );
    case PaymentLinkPaymentStatus.PENDING_PAYMENT:
      return (
        <Tooltip title={translations('calendar.edit_order.paymentLinkStatusPending')} arrow>
          <span style={{ display: 'inline-flex' }}>
            <PaymentLinkWaitingIcon width={24} height={24} />
          </span>
        </Tooltip>
      );

    default:
      return null;
  }
};
export const getLinkToPaymentLink = (paymentLinkId: string) =>
  `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/payment-link?linkid=${paymentLinkId}`;

type EditOrderDetailsProps = {
  errors: FormikState<any>['errors'];
  isLoadingDates: boolean;
  hidePricing?: boolean;
  // Propagates a server-refreshed order up to the calendar's SWR cache.
  onOrderUpdated?: (order: OrderData) => void;
  focusExtraItems?: boolean;
  requestedAction?: { type: ExtraItemsQuickAction; token: number } | null;
};

const OrderDetails = ({
  errors,
  isLoadingDates,
  hidePricing,
  onOrderUpdated,
  focusExtraItems,
  requestedAction,
}: EditOrderDetailsProps) => {
  const { authState } = useAuth();
  const {
    // @ts-ignore
    editOrderState,
    // @ts-ignore
    setEditOrderState,
    // @ts-ignore
    setOrderDetailValue,
    // @ts-ignore
    setCustomOrderDetailValue,
    // @ts-ignore
    setOrderCustomDetails,
    // @ts-ignore
    setPrice,
    // @ts-ignore
    setMultipleTicketsDetails,
  } = useEditOrder();
  const { t } = useTranslation();
  const isMonetaryVoucher = editOrderState.voucherType === VoucherType.MONETARY;
  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  return (
    <Grid container flexDirection="column">
      {isMonetaryVoucher ? (
        <TextField
          type="number"
          size="small"
          label={t('vouchers.amount')}
          value={editOrderState.details.price || ''}
          disabled={!!editOrderState.orderOrVoucher}
          onChange={(e: any) => setPrice(+e.target.value || 0)}
          InputProps={{ endAdornment: currencySymbol }}
          sx={{ maxWidth: 200 }}
        />
      ) : (
        <UiObjectsList
          type="edit"
          isLoadingDates={isLoadingDates}
          orderState={editOrderState}
          setOrderState={setEditOrderState}
          setOrderDetailValue={setOrderDetailValue}
          setCustomOrderDetailValue={setCustomOrderDetailValue}
          setMultipleTicketsDetails={setMultipleTicketsDetails}
          setOrderCustomDetails={setOrderCustomDetails}
          errors={errors}
          isEditOrder
        />
      )}

      {!hidePricing && (
        <PriceBox onOrderUpdated={onOrderUpdated} focusExtraItems={focusExtraItems} requestedAction={requestedAction} />
      )}
    </Grid>
  );
};

export default OrderDetails;
